import { getBaseUrl } from "@Front/Api/helpers/configHelpers";
import { apiV1Paths } from "@Front/Api/helpers/apiClientV1Paths";
import { fetchWithNetworkLogging } from "@Front/Api/helpers/requestHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";

export type TokenRefreshFailureKind = "network" | "http" | "persist" | "payload" | "transient";

export interface TokenRefreshResult {
	success: boolean;
	httpStatus?: number;
	failureKind?: TokenRefreshFailureKind;
}

class TokenRefreshService {
	/**
	 * Single-flight guard: one refresh request in flight per endpoint. Concurrent callers
	 * (parallel API calls, multiple components) await the same promise instead of racing the
	 * backend rotation (a losing concurrent rotation gets "jeton révoqué" → auto-logout loops).
	 */
	private static inFlightRefreshByEndpoint = new Map<string, Promise<TokenRefreshResult>>();

	private static resolveEndpointFromPath(path: string): string {
		const baseUrl = getBaseUrl().replace(/\/$/, "");
		const normalizedPath = path.replace(/^\/+/, "");
		return `${baseUrl}/${normalizedPath}`;
	}

	private static async fetchAndPersistToken(
		endpoint: string,
		refreshToken: string,
		consumer: (accessToken: string, refreshToken: string) => Promise<boolean>,
	): Promise<TokenRefreshResult> {
		const inFlight = this.inFlightRefreshByEndpoint.get(endpoint);
		if (inFlight) {
			LoggerService.info("ℹ️ [TokenRefreshService] Refresh déjà en cours, attente du résultat partagé", { endpoint });
			return inFlight;
		}
		const refreshPromise = this.executeFetchAndPersistToken(endpoint, refreshToken, consumer).finally(() => {
			this.inFlightRefreshByEndpoint.delete(endpoint);
		});
		this.inFlightRefreshByEndpoint.set(endpoint, refreshPromise);
		return refreshPromise;
	}

	/** Token refresh is a background flow: transport failures must never show the network toast. */
	private static async performRefreshRequest(endpoint: string, refreshToken: string): Promise<Response> {
		const headers = new Headers();
		headers.append("Authorization", `Bearer ${refreshToken}`);
		return fetchWithNetworkLogging(endpoint, { method: "POST", headers }, { background: true });
	}

	private static async executeFetchAndPersistToken(
		endpoint: string,
		refreshToken: string,
		consumer: (accessToken: string, refreshToken: string) => Promise<boolean>,
	): Promise<TokenRefreshResult> {
		try {
			const response = await this.performRefreshRequest(endpoint, refreshToken);

			if (!response.ok) {
				LoggerService.error("❌ [TokenRefreshService] Refresh token request failed", {
					endpoint,
					status: response.status,
				});
				return { success: false, httpStatus: response.status, failureKind: "http" };
			}

			const payload = (await response.json()) as { accessToken?: string; refreshToken?: string };
			if (!payload?.accessToken) {
				LoggerService.error("❌ [TokenRefreshService] Missing accessToken in refresh response", { endpoint });
				return { success: false, failureKind: "payload" };
			}

			const nextRefreshToken = payload.refreshToken ?? refreshToken;
			if (!payload.refreshToken) {
				LoggerService.warn("⚠️ [TokenRefreshService] Refresh token rotation not provided, reusing previous token", {
					endpoint,
				});
			}

			const persisted = await consumer(payload.accessToken, nextRefreshToken);
			return persisted ? { success: true } : { success: false, failureKind: "persist" };
		} catch (error) {
			LoggerService.error("❌ [TokenRefreshService] Failed to refresh token", { endpoint, error });
			return { success: false, failureKind: "network" };
		}
	}

	public static async refreshUserToken(refreshToken: string): Promise<TokenRefreshResult> {
		const endpoint = this.resolveEndpointFromPath(apiV1Paths.idnot.refreshToken);
		return this.fetchAndPersistToken(endpoint, refreshToken, UserStore.instance.connect.bind(UserStore.instance));
	}

	public static async refreshCustomerToken(refreshToken: string): Promise<TokenRefreshResult> {
		const endpoint = this.resolveEndpointFromPath(apiV1Paths.customer.refreshToken);
		return this.fetchAndPersistToken(endpoint, refreshToken, CustomerStore.instance.connect.bind(CustomerStore.instance));
	}

	public static async refreshThirdPartyToken(refreshToken: string): Promise<TokenRefreshResult> {
		const endpoint = this.resolveEndpointFromPath(apiV1Paths.public.thirdPartyRefreshToken);
		return this.fetchAndPersistToken(endpoint, refreshToken, ThirdPartyStore.instance.connect.bind(ThirdPartyStore.instance));
	}
}

// Export named only - no default export to avoid Webpack initialization order issues
export { TokenRefreshService };
